import mapboxgl from 'mapbox-gl';
import { merge, each, isArray, debounce } from 'lodash';

const breakIntoTwoLines = (input) => {
  if (input.length < 15) {
    return {
      firstLine: input,
      secondLine: '',
    };
  }

  const words = input.split(' ');
  let midPoint = Math.ceil(input.length / 2);
  if (words[0].length > midPoint) {
    midPoint = words[0].length;
  }
  let firstLine = '';
  let secondLine = '';
  let useSecondLine = false;
  words.forEach((word) => {
    if (firstLine.length + word.length <= midPoint && !useSecondLine) {
      firstLine = `${firstLine} ${word}`.trim();
    } else {
      useSecondLine = true;
      secondLine = `${secondLine} ${word}`.trim();
    }
  });
  return {
    firstLine,
    secondLine,
  };
};

class SurreyMap {
  constructor(config = {}) {
    const defaultConfig = {
      containerId: 'map',
      mapboxToken: process.env.MAPBOX_KEY,
      zooms: {
        holistic: window.matchMedia('(max-width: 600px)').matches ? 9.5 : 10.5,
        regional: 12,
        local: 16.5,
        macro: 0,
      },
    };
    this.config = merge(defaultConfig, config);
    this.metaData = null;
    this.markersMap = {};
    this.hiddenMarkersMap = {};
    this.popupToReopen = null;
    this.popupMap = {};
    this.paths = [];

    this.markers = [];
    this.markerLayerIds = new Set();
    this.routeLayerIds = new Map();
    this.boundaryLayerIds = new Set();
    this.debouncedUpdateMarkers = debounce(this.updateMarkers.bind(this), 200);
    mapboxgl.accessToken = this.config.mapboxToken;
  }

  initMap(metaData, nav) {
    return new Promise((resolve) => {
      this.metaData = metaData;
      this.map = new mapboxgl.Map({
        container: this.config.containerId,
        style: process.env.MAPBOX_STYLE_URL,
        center: [-122.85000000000002, 49.183333],
        zoom: 0,
        maxZoom: 24,
      });
      this.map.on('contextmenu', (e) => {
        const { lat, lng } = e.lngLat;
        const input = document.createElement('input');
        input.value = `${lat},${lng},${this.map.getZoom()}`;
        document.body.appendChild(input);
        input.select();
        input.setSelectionRange(0, 99999); /* For mobile devices */
        navigator.clipboard.writeText(input.value);
        input.remove();
      });
      this.map.on('moveend', (e) => {
        // Workaround as things like fullscreen videos were triggering this
        if (e.originalEvent) {
          this.debouncedUpdateMarkers();
        }
      });
      this.map.on('load', () => {
        const layers = this.map.getStyle().layers;
        this.addSurreyBoundary();
        this.addUSBorder();
        this.addNavLayers(nav);

        let labelLayerId;
        for (let i = 0; i < layers.length; i += 1) {
          if (layers[i].type === 'symbol' && layers[i].layout['text-field']) {
            labelLayerId = layers[i].id;
            break;
          }
        }

        this.map.addLayer(
          {
            id: '3d-buildings',
            source: 'composite',
            'source-layer': 'building',
            filter: ['==', 'extrude', 'true'],
            type: 'fill-extrusion',
            minzoom: 15,
            paint: {
              'fill-extrusion-color': '#ccc',

              // use an 'interpolate' expression to add a smooth transition effect to the
              // buildings as the user zooms in
              'fill-extrusion-height': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15,
                0,
                15.05,
                ['get', 'height'],
              ],
              'fill-extrusion-base': [
                'interpolate',
                ['linear'],
                ['zoom'],
                15,
                0,
                15.05,
                ['get', 'min_height'],
              ],
              'fill-extrusion-opacity': 1,
            },
          },
          labelLayerId,
        );
        resolve();
      });
    });
  }

  updateMarkers() {
    this.removeMarkers();
    const markerLayers = [...this.markerLayerIds].flatMap(id =>
      this.map.querySourceFeatures(id),
    );

    markerLayers.forEach((feature) => {
      const {
        cluster,
        id,
        cluster_id: clusterId,
        point_count: pointCount,
        title,
        markerImage,
      } = feature.properties;
      let markerId = id;
      if (!id && !isNaN(clusterId)) {
        markerId = `marker-${clusterId}`;
      }
      const coords = feature.geometry.coordinates;
      const el = document.createElement('div');
      el.classList.add('marker');
      if (cluster) {
        el.classList.add('marker--cluster');
        const text = document.createElement('span');
        text.classList.add('marker__count');
        text.textContent = pointCount;
        el.appendChild(text);
      } else {
        el.classList.add('marker--unclustered');
        if (id === this.lastSlugNavigatedTo) {
          el.classList.add('marker--active');
        }
        const icon = document.createElement('i');
        icon.classList.add('marker__icon');
        icon.classList.add(`marker__icon--${markerImage}`);
        el.appendChild(icon);

        const label = document.createElement('span');
        label.classList.add('marker__label');
        const { firstLine, secondLine } = breakIntoTwoLines(title);
        const firstElement = document.createElement('p');
        firstElement.textContent = firstLine;
        label.appendChild(firstElement);
        if (secondLine && secondLine.length) {
          const secondElement = document.createElement('p');
          secondElement.textContent = secondLine;
          label.appendChild(secondElement);
        }
        el.appendChild(label);
      }

      if (markerId) {
        if (this.markersMap[markerId]) {
          this.markersMap[markerId].remove();
          delete this.markersMap[markerId];
        }
        this.markersMap[markerId] = new mapboxgl.Marker(el)
          .setLngLat(coords)
          .addTo(this.map);
        if (this.popupMap[markerId]) {
          this.markersMap[markerId].setPopup(this.popupMap[markerId]);
          if (this.popupToReopen === markerId) {
            setTimeout(() => {
              SurreyMap.openPopup(this.markersMap[markerId]);
            });

            this.popupToReopen = null;
          }
        }
      }
    });
  }

  addOverviewMarkers(parents) {
    parents.forEach((parent) => {
      if (parent.overview()) {
        const overview = parent
          .children()
          .list()
          .find(child => child.id() === parent.overview());
        if (overview && overview.showPopup()) {
          const el = document.createElement('div');
          el.classList.add('hidden-marker');
          const markerId = parent.overview();
          const coords = new mapboxgl.LngLat(overview.lon(), overview.lat());
          if (this.hiddenMarkersMap[markerId]) {
            this.hiddenMarkersMap[markerId].remove();
            delete this.hiddenMarkersMap[markerId];
          }
          this.hiddenMarkersMap[markerId] = new mapboxgl.Marker(el)
            .setLngLat(coords)
            .addTo(this.map);
          if (this.popupMap[markerId]) {
            this.hiddenMarkersMap[markerId].setPopup(this.popupMap[markerId]);
          }
        }
      }
    });
  }

  addMarkerLayer(id, children = [], maxZoom = 14, markerClass) {
    this.markerLayerIds.add(id);
    const features = children.map(link => ({
      type: 'Feature',
      properties: {
        id: `${link.slug()}`,
        title: link.title(),
        markerImage: markerClass || link.markerClass(),
      },
      geometry: {
        type: 'Point',
        coordinates: [link.lon(), link.lat()],
      },
    }));
    const data = {
      type: 'FeatureCollection',
      features,
    };
    this.map.addSource(id, {
      type: 'geojson',
      data,
      cluster: true,
      clusterMaxZoom: maxZoom,
    });

    this.map.addLayer({
      id: `${id}-clusters`,
      type: 'circle',
      source: id,
      filter: ['has', 'point_count'],
      paint: {
        'circle-radius': 0,
      },
      layout: {
        visibility: 'none',
      },
    });

    this.map.addLayer({
      id: `${id}-unclustered-points`,
      type: 'circle',
      source: id,
      filter: ['!has', 'point_count'],
      paint: {
        'circle-radius': 0,
      },
      layout: {
        visibility: 'none',
      },
    });

    children.forEach((child) => {
      if (child.showPopup() && !this.popupMap[child.slug()]) {
        this.popupMap[child.slug()] = new mapboxgl.Popup({

          offset: 16,
        }).setHTML(SurreyMap.buildPopup(child));
      }
    });
  }

  toggleRouteLayerVisibility(id) {
    this.routeLayerIds.forEach((routeLayer) => {
      routeLayer.forEach((routeId) => {
        this.map.setLayoutProperty(`route-${routeId}`, 'visibility', 'none');
      });
    });
    if (this.routeLayerIds.has(id)) {
      this.routeLayerIds.get(id).forEach((routeId) => {
        // routeLayer.forEach((routeId) => {
        this.map.setLayoutProperty(`route-${routeId}`, 'visibility', 'visible');
        // });
      });
    }
  }

  toggleBoundaryLayerVisibility(id) {
    this.boundaryLayerIds.forEach((layerId) => {
      let visibility = 'none';

      if (layerId.startsWith(id)) {
        visibility = 'visible';
      }

      this.map.setLayoutProperty(
        `boundary-fill-${layerId}`,
        'visibility',
        visibility,
      );
      this.map.setLayoutProperty(
        `boundary-outline-${layerId}`,
        'visibility',
        visibility,
      );
    });

    setTimeout(() => {
      this.updateMarkers();
    }, 800);
  }

  toggleAllBoundaryLayers(visibility = 'none') {
    this.boundaryLayerIds.forEach((layerId) => {
      this.map.setLayoutProperty(
        `boundary-fill-${layerId}`,
        'visibility',
        visibility,
      );
      this.map.setLayoutProperty(
        `boundary-outline-${layerId}`,
        'visibility',
        visibility,
      );
    });
  }

  toggleMarkerLayerVisibility(id) {
    this.markerLayerIds.forEach((layerId) => {
      let visibility = 'none';

      if (id && layerId === id) {
        visibility = 'visible';
      }

      this.map.setLayoutProperty(
        `${layerId}-clusters`,
        'visibility',
        visibility,
      );
      this.map.setLayoutProperty(
        `${layerId}-unclustered-points`,
        'visibility',
        visibility,
      );
    });
    this.updateMarkers();
  }

  addNavLayers(nav) {
    nav.list().forEach((parent) => {
      const zoom = (parent.navigateTo() || '')
        .split(',')
        .map(segment => Number(segment))
        .slice(-1);
      const maxZoom = Math.floor(zoom) + 1;
      this.addMarkerLayer(parent.slug(), parent.children().list(), maxZoom);
      this.addCategoryBoundaryLayer(
        parent.slug(),
        parent.boundaryCoordinates(),
        parent.colour(),
      );
      this.routeLayerIds.set(parent.slug(), new Set());
      parent
        .children()
        .list()
        .forEach((child) => {
          if (child.type() === 'group' && child.hasChildren()) {
            this.addMarkerLayer(child.slug(), child.children().list());
          } else if (child.type() === 'route') {
            this.routeLayerIds.set(child.slug(), new Set());
            this.addRouteLayer(child.slug(), parent.slug(), child.path());
            this.addMarkerLayer(child.slug(), child.children().list(), 14, child.markerClass());
            if (child.showPopup()) {
              const el = document.createElement('div');
              el.classList.add('hidden-marker');
              const markerId = child.slug();
              const coords = new mapboxgl.LngLat(child.lon(), child.lat());
              this.hiddenMarkersMap[markerId] = new mapboxgl.Marker(el)
                .setLngLat(coords)
                .addTo(this.map);
              this.popupMap[child.slug()] = new mapboxgl.Popup({
                offset: 16,
              }).setHTML(SurreyMap.buildPopup(child));
              this.hiddenMarkersMap[markerId].setPopup(this.popupMap[markerId]);
            }
          } else {
            this.addMarkerLayer(child.slug(), [child]);
          }
          if (child.hasChildren()) {
            child
              .children()
              .list()
              .forEach((grandchild) => {
                if (grandchild.type() === 'group') {
                  this.addMarkerLayer(
                    grandchild.slug(),
                    grandchild.children().list(),
                  );
                } else if (grandchild.type() === 'route') {
                  this.routeLayerIds.set(grandchild.slug(), new Set());
                  this.addRouteLayer(
                    grandchild.slug(),
                    parent.slug(),
                    grandchild.path(),
                  );
                  this.addMarkerLayer(
                    grandchild.slug(),
                    grandchild.children().list(),
                  );
                } else {
                  this.addMarkerLayer(grandchild.slug(), [grandchild]);
                }
              });
          }
        });
    });

    this.addOverviewMarkers(nav.list());
  }

  addSurreyBoundary() {
    this.map.addLayer({
      id: 'surrey-boundary',
      type: 'line',
      source: {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [-122.679518, 49.18682],
              [-122.680104, 49.002137],
              [-122.757783, 49.002048],
              [-122.779674, 49.012915],
              [-122.779132, 49.031033],
              [-122.845389, 49.031215],
              [-122.845333, 49.024396],
              [-122.866775, 49.024553],
              [-122.874993, 49.029889],
              [-122.884522, 49.044231],
              [-122.888055, 49.057215],
              [-122.87645, 49.062716],
              [-122.870516, 49.056758],
              [-122.859585, 49.067559],
              [-122.861817, 49.0869],
              [-122.890656, 49.091903],
              [-122.890141, 49.177094],
              [-122.9231, 49.176982],
              [-122.878211, 49.219438],
              [-122.816842, 49.220027],
              [-122.729809, 49.211505],
              [-122.723115, 49.189633],
              [-122.70921, 49.18341],
              [-122.679518, 49.18682],
            ], // eslint-disable-line max-len
          },
        },
      },
      layout: {
        'line-join': 'round',
        'line-cap': 'butt',
      },
      paint: {
        'line-color': '#266E7F',
        'line-width': 2,
        'line-dasharray': [2, 2],
      },
    });
  }

  addUSBorder() {
    this.map.addLayer({
      id: 'surrey-us-border',
      type: 'line',
      source: {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [-95.153698, 49.001343],
              [-122.757206, 49.001343],
            ],
          },
        },
      },
      layout: {
        'line-join': 'round',
        'line-cap': 'butt',
      },
      paint: {
        'line-color': '#FFFFFF',
        'line-width': 0.75,
      },
    });
  }

  destroy() {
    this.removeMarkers();
    this.map.remove();
    const mapNode = document.getElementById(this.config.containerId);
    while (mapNode.firstChild) {
      mapNode.removeChild(mapNode.firstChild);
    }
  }

  flyTo(coords, zoom = 'local', zoomValue) {
    const numberZoom = zoomValue || (isNaN(zoom) ? this.config.zooms[zoom] : zoom);
    return new Promise((resolve) => {
      this.flying = true;
      this.map.flyTo({
        center: coords,
        zoom: numberZoom,
        speed: 1.2,
        pitch: zoom === 'local' ? 60 : 0,
      });

      this.map.once('moveend', () => {
        this.flying = false;
        resolve();
      });
    });
  }

  fitBounds(bounds, zoom = 'regional') {
    return new Promise((resolve) => {
      this.flying = true;
      this.map.fitBounds(bounds, {
        pitch: zoom === 'local' ? 60 : 0,
        padding: 300,
      });
      this.map.once('moveend', () => {
        this.flying = false;
        resolve();
      });
    });
  }

  fadeAllBut(link) {
    each(this.paths, (path) => {
      let opacity = 0;
      if (link && path.indexOf(link.slug()) > -1) {
        opacity = 1;
      }
      this.map.setPaintProperty(`route-${path}`, 'line-opacity', opacity);
    });
    each(this.markersMap, (marker, key) => {
      if (link && key.indexOf(link.slug()) > -1) {
        if (
          !(
            (link.type() === 'group' || link.type() === 'route') &&
            (key === link.slug() ||
              key === `${link.slug()}-start` ||
              key === `${link.slug()}-end`)
          )
        ) {
          marker.getElement().classList.remove('hidden');
          marker.getElement().classList.add('active');
        } else {
          marker.getElement().classList.add('hidden');
          marker.getElement().classList.remove('active');
        }
      } else {
        marker.getElement().classList.add('hidden');
        marker.getElement().classList.remove('active');
      }
    });
  }

  async setDestination(link, layersSlug) {
    if (this.eventsPopup) {
      this.eventsPopup.remove();
    }

    this.closePopups();
    const [lat, lon, zoom] = link
      .navigateTo()
      .split(',')
      .map(segment => Number(segment));
    const newZoom = window.matchMedia('(max-width: 600px)').matches ? zoom - 2 : zoom;
    await this.flyTo([lon, lat], link.view(), newZoom);
    this.toggleMarkerLayerVisibility(layersSlug || link.slug());
    this.toggleRouteLayerVisibility(layersSlug || link.slug());
    this.lastSlugNavigatedTo = link.slug();
    this.debouncedUpdateMarkers();
    setTimeout(() => {
      if (link.showPopup() && this.markersMap[link.slug()]) {
        SurreyMap.openPopup(this.markersMap[link.slug()]);
      } else if (link.showPopup() && this.hiddenMarkersMap[link.slug()]) {
        SurreyMap.openPopup(this.hiddenMarkersMap[link.slug()]);
      }
    }, 500);
  }

  async setCategory(link) {
    if (this.eventsPopup) {
      this.eventsPopup.remove();
    }
    this.removeMarkers();
    this.removePath();

    const [lat, lon, zoom] = link
      .navigateTo()
      .split(',')
      .map(segment => Number(segment));
    const newZoom = window.matchMedia('(max-width: 600px)').matches ? zoom - 2 : zoom;
    await this.flyTo([lon, lat], link.view(), newZoom);
    const markerSlug = link.overview() ? link.overview() : link.slug();
    const overview = link
      .children()
      .list()
      .find(child => child.id() === link.overview());
    this.toggleMarkerLayerVisibility(markerSlug);
    this.toggleRouteLayerVisibility(markerSlug);
    this.toggleBoundaryLayerVisibility(link.slug());
    this.debouncedUpdateMarkers();
    if (overview) {
      setTimeout(() => {
        if (overview.showPopup() && this.hiddenMarkersMap[overview.slug()]) {
          SurreyMap.openPopup(this.hiddenMarkersMap[overview.slug()]);
        }
      }, 500);
    }
  }

  removePath() {
    this.paths.forEach((id) => {
      if (this.map.getLayer(`route-${id}`)) {
        this.map.removeSource(`route-${id}`);
        this.map.removeLayer(`route-${id}`);
      }
    });
    this.paths = [];
  }

  closePopups() {
    each({ ...this.markersMap, ...this.hiddenMarkersMap }, marker => SurreyMap.closePopup(marker));
  }

  removeMarkers() {
    each(this.markersMap, (marker, key) => {
      if (marker && marker.getPopup() && marker.getPopup().isOpen()) {
        this.popupToReopen = key;
      }
      marker.remove();
      delete this.markersMap[key];
    });
  }

  addCategoryBoundaryLayer(categoryId, coordinates, colour) {
    if (coordinates && coordinates.length > 0) {
      coordinates.forEach((coordinate, index) => {
        const id = `${categoryId}-${index}`;
        this.map.addSource(`boundary-${id}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [coordinate],
            },
          },
        });

        this.map.addLayer({
          id: `boundary-fill-${id}`,
          type: 'fill',
          source: `boundary-${id}`,
          layout: {
            visibility: 'none',
          },
          paint: {
            'fill-color': colour,
            'fill-opacity': 0.1,
          },
        });
        this.map.addLayer({
          id: `boundary-outline-${id}`,
          type: 'line',
          source: `boundary-${id}`,
          layout: {
            visibility: 'none',
          },
          paint: {
            'line-color': colour,
            'line-width': 0.5,
          },
        });
        this.boundaryLayerIds.add(id);
      });
    }
  }

  addRouteLayer(id, parentId, path) {
    let features = [];
    if (isArray(path.route)) {
      features = [path.route];
    } else if (isArray(path.route.features)) {
      features = path.route.features;
    }
    features.forEach((feature, index) => {
      const idString = `${id}${index}`;
      // this.paths.push(`${id}${index}`);
      this.routeLayerIds.get(id).add(idString);
      this.routeLayerIds.get(parentId).add(idString);
      const layerConfig = {
        id: `route-${idString}`,
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: isArray(feature) ? feature : feature.route,
            },
          },
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'butt',
          visibility: 'none',
        },
        paint: {
          'line-color': feature.routeColour || path.routeColour || '#FFA612',
          'line-width': path.routeLineWidth ? path.routeLineWidth : 2,
        },
      };
      if (
        (!isArray(feature) && feature.routeDashed === true) ||
        (isArray(feature) && path.routeDashed === true)
      ) {
        // eslint-disable-line max-len
        layerConfig.paint['line-dasharray'] = [
          path.routeDashWidth || 3,
          path.routeDashGap || 1,
        ];
      }
      this.map.addLayer(layerConfig);
    });
  }

  static getBounds(link) {
    const bounds = new mapboxgl.LngLatBounds();
    const points = link
      .children()
      .list()
      .reduce((acc, item) => {
        let newAcc = acc;
        newAcc.push(new mapboxgl.LngLat(item.lon(), item.lat()));
        if (item.hasPath()) {
          newAcc = [...acc, ...SurreyMap.getPathBounds(item.path().route)];
        }
        return newAcc;
      }, []);

    points.forEach(point => bounds.extend(point));
    return bounds;
  }

  static getPathBounds(path) {
    const bounds = new mapboxgl.LngLatBounds();
    let paths = [];
    if (isArray(path)) {
      paths = [path];
    } else if (isArray(path.features)) {
      paths = path.features.map(item => item.route);
    }
    const points = paths.reduce(
      (acc, pathFeature) => [
        ...acc,
        ...pathFeature.map(point => new mapboxgl.LngLat(point[0], point[1])),
      ],
      [],
    ); // eslint-disable-line max-len
    points.forEach(point => bounds.extend(point));
    return bounds;
  }

  static getGroupBounds(group) {
    const bounds = new mapboxgl.LngLatBounds();
    const points = group
      .list()
      .map(point => new mapboxgl.LngLat(point.lon(), point.lat()));
    points.forEach(point => bounds.extend(point));
    return bounds;
  }

  static openPopup(marker) {
    if (marker.getPopup() && marker.getPopup().isOpen() === false) {
      marker.togglePopup();
    }
  }

  static closePopup(marker) {
    if (marker.getPopup() && marker.getPopup().isOpen() === true) {
      marker.togglePopup();
    }
  }

  static buildPopupGallery(images, alt = '') {
    let galleryMarkup = '<div class="popup__gallery">';
    galleryMarkup += images.reduce((html, image) => {
      const img =
        image && typeof image === 'string' && image.length > 4
          ? `<img src="${image}" alt="${alt}" onclick="window.lightbox('${image}')">`
          : ''; // eslint-disable-line max-len
      return `${html}${img}`;
    }, '');
    galleryMarkup += '</div>';
    return galleryMarkup;
  }

  static buildPopup(link) {
    const details = link.details();
    const isOverview = link.title().trim().toLowerCase() === 'overview';
    let popupMarkup = `
      <h1>${isOverview ? link.parentTitle() : link.title()}</h1>
      ${isOverview ? '' : `<h2 class="subtitle--${link.parentSlug()}">${link.parentTitle()}</h2>`}
    `;
    if (details.video) {
      popupMarkup += `
        <video controls>
          <source src="${details.video}" type="video/mp4">
        </video>
      `;
    } else if (
      details.images &&
      details.images.length > 1 &&
      details.images[0] &&
      details.images[0].length > 4
    ) {
      // eslint-disable-line max-len
      popupMarkup += `
        <img class="image--large" src="${details.images[0]}" alt="${link.title}">
      `;
    }

    if (details.text) {
      popupMarkup += `<p>${details.text}</p>`;
    }

    if (details.images && details.images.length > 0) {
      popupMarkup += SurreyMap.buildPopupGallery(details.images);
    }
    return popupMarkup;
  }
}

export default SurreyMap;



// WEBPACK FOOTER //
// ./src/services/Map.js